import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  TextInput,
  PasswordInput,
  Button,
  Container,
  Alert,
  Anchor,
  Title,
  Stack,
  Image,
  Box,
  Paper,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useAuthStore } from '../../../application/store/authStore';
import { AcceptInviteCommand } from '../../../application/dto/auth/AcceptInviteCommand';
import { PUBLIC_ROUTES } from '../../routing/routes';
import LogoSvg from '../../../assets/images/logo.svg';

interface AcceptInviteFormData {
  email: string;
  honeypot: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * AcceptInvitePage — MineMiner SPA invite-acceptance screen.
 *
 * sprint-108 S1 (post-epic-013 user-lifecycle). Mirrors `ResetPasswordPage`
 * shape verbatim (including the Hotfix-#6 lessons: `validateInputOnChange`
 * surfaces inline policy errors as the user types, and the submit button is
 * NEVER `disabled` on form invalidity — submitting on an invalid form is what
 * triggers the error display).
 *
 * Reads `?uid=` + `?token=` from the URL query string (matches the link
 * MineMinerInviteUserCommandHandler builds against `MineMiner:RepBaseUrl`).
 * sprint-281 (S001/D2): the link carries the user id instead of the email, so
 * no Email field is shown. A link sent before that release still carries
 * `?email=` and gets the old pre-filled Email field.
 * Token is the dedicated-purpose "Invite" token from
 * `MineMinerInviteTokenProvider` (14-day lifespan); validation happens
 * server-side via `UserManager.VerifyUserTokenAsync`. POSTs the
 * `MineMinerAcceptInviteCommand` shape to `/api/mineminer/auth/accept-invite`
 * (anonymous + `InvitePolicy` rate-limited 5/hr/IP).
 *
 * Bot-protection guards (honeypot + form-load-timestamp) are carried verbatim
 * from the forgot-password page since this endpoint is also anonymous and
 * bot-exposed.
 */
export const AcceptInvitePage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const acceptInvite = useAuthStore((state) => state.acceptInvite);
  const acceptInviteState = useAuthStore((state) => state.acceptInviteState);
  const acceptInviteError = useAuthStore((state) => state.acceptInviteError);

  const [tokenError, setTokenError] = useState<string | null>(null);

  // Capture form-load timestamp once on first render. Bot-protection compares
  // this to submit time; legitimate users take >3s to fill in a password.
  const formLoadTimestampRef = useRef<number>(Date.now());

  const token = searchParams.get('token') ?? '';
  const userId = searchParams.get('uid') ?? '';
  const emailFromUrl = searchParams.get('email') ?? '';

  const form = useForm<AcceptInviteFormData>({
    // sprint-107 SESSION_004 Hotfix #6 carried over — surface inline policy
    // errors per-keystroke so the user can see *which* requirement isn't met.
    validateInputOnChange: true,
    initialValues: {
      email: emailFromUrl,
      honeypot: '',
      newPassword: '',
      confirmPassword: '',
    },
    validate: {
      email: (value: string): string | null => {
        if (userId) {
          return null;
        }
        if (!value || value.trim().length === 0) {
          return 'Email is required';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return 'Invalid email format';
        }
        return null;
      },
      // Mirrors MineMinerIdentityServiceExtensions password policy
      // (MineMinerIdentityServiceExtensions.cs:36-41).
      newPassword: (value: string): string | null => {
        if (!value || value.trim().length === 0) {
          return 'Password is required';
        }
        if (value.length < 12) {
          return 'Password must be at least 12 characters';
        }
        if (!/[A-Z]/.test(value)) {
          return 'Password must contain at least one uppercase letter';
        }
        if (!/[a-z]/.test(value)) {
          return 'Password must contain at least one lowercase letter';
        }
        if (!/[0-9]/.test(value)) {
          return 'Password must contain at least one number';
        }
        if (!/[^A-Za-z0-9]/.test(value)) {
          return 'Password must contain at least one non-alphanumeric character';
        }
        return null;
      },
      confirmPassword: (value: string, values: AcceptInviteFormData): string | null => {
        if (value !== values.newPassword) {
          return 'Passwords do not match';
        }
        return null;
      },
    },
  });

  useEffect(() => {
    if (emailFromUrl) {
      form.setFieldValue('email', emailFromUrl);
    }
    // form is stable across renders; intentionally omit from deps to avoid
    // re-running on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailFromUrl]);

  useEffect(() => {
    if (!token) {
      setTokenError('Invalid or missing invite token. Ask your admin to send a new invite.');
    } else {
      setTokenError(null);
    }
  }, [token]);

  useEffect(() => {
    if (acceptInviteState === 'acceptSuccess') {
      const timer = setTimeout(() => {
        navigate(PUBLIC_ROUTES.LOGIN);
      }, 3000);
      return (): void => clearTimeout(timer);
    }
    return undefined;
  }, [acceptInviteState, navigate]);

  const handleSubmit = async (values: AcceptInviteFormData): Promise<void> => {
    if (!token) {
      return;
    }

    const command: AcceptInviteCommand = {
      ...(userId ? { userId } : { email: values.email }),
      token,
      newPassword: values.newPassword,
      honeypot: values.honeypot,
      formLoadTimestamp: formLoadTimestampRef.current,
    };

    try {
      await acceptInvite(command);
      form.reset();
    } catch {
      // Error state is captured in the auth store; the Alert below renders it.
    }
  };

  const showError = tokenError !== null
    || (acceptInviteState === 'error' && acceptInviteError !== null);
  const errorMessage = tokenError ?? acceptInviteError;

  return (
    <Box mih="100vh" bg="gray.0" py={80} px="md">
      <Container size="xs">
        <Paper bg="white" p={48} radius="md" shadow="sm" withBorder>
          <Stack gap="md" align="center" mb="md">
            <Image src={LogoSvg} alt="MineMiner" h={48} w="auto" />
            <Title order={1} size="h2" ta="center">
              Accept your invite
            </Title>
            <Text size="sm" c="dimmed" ta="center">
              Set a password to finish creating your MineMiner account.
            </Text>
          </Stack>

          {showError && errorMessage !== null && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
              mb="md"
              title="Error"
            >
              {errorMessage}
            </Alert>
          )}

          {acceptInviteState === 'acceptSuccess' && (
            <Alert
              icon={<IconCheck size={16} />}
              color="green"
              variant="light"
              mb="md"
              title="Invite accepted"
            >
              Your account is set up. Redirecting to the sign-in page&hellip;
            </Alert>
          )}

          {acceptInviteState !== 'acceptSuccess' && tokenError === null && (
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap="md">
                {!userId && (
                  <TextInput
                    label="Email"
                    type="email"
                    placeholder="your@email.com"
                    required
                    size="md"
                    {...form.getInputProps('email')}
                  />
                )}

                {/*
                  Honeypot field — hidden from sighted users via aria-hidden +
                  tabIndex=-1 + Mantine's `display: 'none'` style on the
                  wrapper. Bots that auto-fill all inputs trip the
                  server-side honeypot guard.
                */}
                <TextInput
                  label="Phone"
                  placeholder="Leave this empty"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  display="none"
                  {...form.getInputProps('honeypot')}
                />

                <PasswordInput
                  label="Set password"
                  placeholder="Your new password"
                  required
                  size="md"
                  description="At least 12 characters with upper, lower, number, and a symbol."
                  {...form.getInputProps('newPassword')}
                />

                <PasswordInput
                  label="Confirm password"
                  placeholder="Re-enter your password"
                  required
                  size="md"
                  {...form.getInputProps('confirmPassword')}
                />

                <Button
                  type="submit"
                  size="lg"
                  variant="filled"
                  color="brand"
                  loading={acceptInviteState === 'accepting'}
                  fullWidth
                  mt="sm"
                >
                  Accept invite &amp; set password
                </Button>
              </Stack>
            </form>
          )}

          <Text ta="center" size="sm" mt="md">
            <Anchor component={Link} to={PUBLIC_ROUTES.LOGIN} fw={600}>
              Back to sign in
            </Anchor>
          </Text>
        </Paper>
      </Container>
    </Box>
  );
};
